import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Home, Users, Briefcase, Clock, FileText, Bell, Settings, LogOut, Menu, X,
  ChevronRight, ChevronDown, Plus, CheckCircle, AlertCircle, MapPin, Calendar, User,
  ClipboardList, BarChart3, Play, Square, RefreshCw, Search, Filter, Eye,
  Truck, Camera, Upload, Image, Wrench, Shield, FileImage, Trash2, Save,
  Building2, Sliders, Key, MessageSquare, Send, Car, Fuel, Video, HelpCircle,
  ChevronLeft, Download, Edit3, Check, ArrowLeft, Edit2, UserCheck, Archive, Receipt, BookOpen
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { Badge, Button, Card, Input, Modal, Select, Spinner, Textarea, FileUploadInput } from "../../shared/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const DocumentsView = ({ job, documents, onRefresh, canEdit, onBack }) => {
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [newDocument, setNewDocument] = useState({
    name: "",
    description: "",
    document_type: "other",
    file_url: ""
  });
  const [loading, setLoading] = useState(false);

  const documentTypes = [
    { value: "swms", label: "SWMS" },
    { value: "permit", label: "Permit" },
    { value: "plan", label: "Plan" },
    { value: "photo", label: "Photo" },
    { value: "report", label: "Report" },
    { value: "other", label: "Other" }
  ];

  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!newDocument.file_url) {
      toast.error("Upload a file first, mate — pick one with the Choose File button.");
      return;
    }
    if (!newDocument.name?.trim()) {
      toast.error("Give the document a name");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/documents`, newDocument);
      setNewDocument({ name: "", description: "", document_type: "other", file_url: "" });
      setShowAddDocument(false);
      toast.success("Document added");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add document");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!confirm("Are you sure you want to delete this document?")) return;
    
    try {
      await axios.delete(`${API}/jobs/${job.id}/documents/${documentId}`);
      onRefresh();
    } catch (e) {
      alert("Failed to delete document");
    }
  };

  const getDocumentTypeColor = (type) => {
    const colors = {
      swms: "error",
      permit: "warning", 
      plan: "info",
      photo: "success",
      report: "orange",
      other: "default"
    };
    return colors[type] || "default";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold uppercase">Job Documents</h3>
        {canEdit && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowAddDocument(true)}
            data-testid="add-document-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Document
          </Button>
        )}
      </div>

      {showAddDocument && (
        <Card className="border-orange-500">
          <form onSubmit={handleAddDocument} className="space-y-4">
            <h4 className="font-bold">Add New Document</h4>
            
            <Input
              label="Document Name"
              value={newDocument.name}
              onChange={(e) => setNewDocument({...newDocument, name: e.target.value})}
              required
              data-testid="document-name-input"
            />
            
            <Select
              label="Document Type"
              value={newDocument.document_type}
              onChange={(e) => setNewDocument({...newDocument, document_type: e.target.value})}
              options={documentTypes}
            />
            
            <Textarea
              label="Description"
              value={newDocument.description}
              onChange={(e) => setNewDocument({...newDocument, description: e.target.value})}
              placeholder="Optional description"
            />
            
            <FileUploadInput
              label="Upload File"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
              onFileSelect={(fileUrl, fileName) => setNewDocument({...newDocument, file_url: fileUrl})}
              currentFile={newDocument.file_url ? "File uploaded" : null}
              onClear={() => setNewDocument({...newDocument, file_url: ""})}
            />
            
            <div className="flex gap-2 items-center flex-wrap">
              <Button type="submit" disabled={loading} size="sm" data-testid="submit-add-document">
                {loading ? <Spinner size="sm" /> : "Add Document"}
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm"
                onClick={() => setShowAddDocument(false)}
              >
                Cancel
              </Button>
              {!newDocument.file_url && (
                <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  Upload a file first to enable submit
                </span>
              )}
            </div>
          </form>
        </Card>
      )}

      <div className="max-h-64 overflow-y-auto space-y-2">
        {documents.map(doc => (
          <Card key={doc.id} className="p-4" data-testid={`document-${doc.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-semibold">{doc.name}</h4>
                  <Badge variant={getDocumentTypeColor(doc.document_type)}>
                    {doc.document_type.toUpperCase()}
                  </Badge>
                </div>
                {doc.description && (
                  <p className="text-sm text-slate-600 mb-2">{doc.description}</p>
                )}
                <div className="text-xs text-slate-400">
                  Added by {doc.uploaded_by_name} on {new Date(doc.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {doc.file_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openFile(doc.file_url, doc.name)}
                    data-testid={`view-document-${doc.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                )}
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteDocument(doc.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
        
        {documents.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-semibold">No documents yet</p>
            <p className="text-sm">Add documents to keep track of job-related files</p>
          </div>
        )}
      </div>

      <div className="flex gap-4">
        <Button variant="ghost" onClick={onBack}>Back to Job Details</Button>
      </div>
    </div>
  );
};

// Equipment Schedule Section Component

export default DocumentsView;
